<template>
  <div class="beliefs">
    
    <div class="beliefs-content">
      <!-- Introduction -->
      <section class="beliefs-intro">
        <p class="beliefs-intro__text">
          At Grace Baptist Church Walkerville Walkerville, we hold to the historic Christian faith as revealed in Scripture. 
          Our beliefs are rooted in the Bible, which we believe is the inspired and infallible Word of God.
        </p>
      </section>

      <!-- Core Beliefs -->
      <section class="beliefs-section">
        <h2 class="beliefs-section__title">Core Beliefs</h2>
        <div class="beliefs-grid">
          <div v-for="(belief, index) in coreBeliefs" :key="index" class="belief-card">
            <h3 class="belief-card__title">
              <i :class="belief.icon"></i>
              {{ belief.title }}
            </h3>
            <p class="belief-card__text" v-html="belief.description"></p>
          </div>
        </div>
      </section>

      <!-- Baptist Distinctives -->
      <section class="beliefs-section">
        <h2 class="beliefs-section__title">Baptist Distinctives</h2>
        <p class="beliefs-section__intro">
          As Baptists, we hold to several distinctive principles that have characterized our faith tradition:
        </p>
        <div class="distinctives-list">
          <div v-for="(distinctive, index) in baptistDistinctives" 
               :key="index" 
               class="distinctive-item">
            <h3 class="distinctive-item__title">{{ distinctive.title }}</h3>
            <p class="distinctive-item__text">{{ distinctive.description }}</p>
          </div>
        </div>
      </section>

      <!-- Scripture References -->
      <section class="beliefs-section scripture-section">
        <h2 class="beliefs-section__title">Our Foundation in Scripture</h2>
        <div class="scripture-card">
          <p class="scripture-text">
            "All Scripture is breathed out by God and profitable for teaching, for reproof, 
            for correction, and for training in righteousness."
          </p>
          <p class="scripture-reference">- 2 Timothy 3:16</p>
        </div>
      </section>
    </div>
  </div>
</template>

<script>

export default {
  name: 'BeliefsPage',
  components: {
    
  },
  data() {
    return {
      coreBeliefs: [
        {
          title: 'The Bible',
          icon: 'fas fa-bible',
          description: 'We believe the Bible is God\'s Word, verbally inspired and inerrant in the original manuscripts, and the supreme authority in faith and life.'
        },
        {
          title: 'God',
          icon: 'fas fa-crown',
          description: 'We believe in one God, eternally existing in three persons: Father, Son, and Holy Spirit, equal in every divine perfection.'
        },
        {
          title: 'Jesus Christ',
          icon: 'fas fa-cross',
          description: 'We believe in the deity of Jesus Christ, His virgin birth, sinless life, substitutionary death, bodily resurrection, and His imminent return.'
        },
        {
          title: 'Holy Spirit',
          icon: 'fas fa-dove',
          description: 'We believe in the Holy Spirit who convicts, regenerates, indwells, and empowers believers for godly living and service.'
        },
        {
          title: 'Salvation',
          icon: 'fas fa-heart',
          description: 'We believe salvation is by grace alone through faith alone in Christ alone, not by works, and is eternal and secure in Christ.'
        },
        {
          title: 'The Church',
          icon: 'fas fa-church',
          description: 'We believe the local church is a body of baptized believers, autonomous under the Lordship of Christ, gathering for worship, fellowship, and service.'
        }
      ],
      baptistDistinctives: [
        {
          title: 'Biblical Authority',
          description: 'The Bible is our sole authority for faith and practice, without addition from creeds or traditions.'
        },
        {
          title: 'Autonomy of the Local Church',
          description: 'Each church is self-governing under the Lordship of Christ, free from external control.'
        },
        {
          title: 'Priesthood of All Believers',
          description: 'Every believer has direct access to God through Christ and can read and interpret Scripture with the Holy Spirit\'s guidance.'
        },
        {
          title: 'Two Ordinances',
          description: 'We practice believer\'s baptism by immersion and the Lord\'s Supper as memorial ordinances.'
        },
        {
          title: 'Individual Soul Liberty',
          description: 'Every person has the right and responsibility to personally relate to God.'
        },
       
        {
          title: 'Separation of Church and State',
          description: 'We believe in religious freedom and the distinction between civil and religious authority.'
        }
      ]
    }
  }
}
</script>

<style lang="scss" scoped>
// Variables
$primary-color: #1e40af;
$secondary-color: #60a5fa;
$text-dark: #1f2937;
$text-light: #4b5563;
$white: #ffffff;
$spacing-base: 1rem;
$border-radius: 0.5rem;

// Mixins
@mixin card-shadow {
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

@mixin section-spacing {
  margin-bottom: $spacing-base * 3;
}

// Navigation
.nav {
  background-color: $white;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  position: fixed;
  width: 100%;
  top: 0;
  z-index: 50;
  
  &__container {
    max-width: 64rem;
    margin: 0 auto;
    padding: 1rem;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  &__logo {
    text-decoration: none;
    
    &-img {
      height: 50px; // Adjust this value based on your logo size
      width: auto;
    }
  }

  &__links {
    display: flex;
    gap: 1.5rem;
  }

  &__link {
    color: $text-dark;
    text-decoration: none;
    font-weight: 500;
    transition: all 0.3s ease;

    &:hover {
      color: $primary-color;
    }

    &.router-link-active {
      color: $primary-color;
    }
  }
}

// Hero Section
.beliefs-hero {
  padding-top: 4rem;
  background-color: $primary-color;
  color: $white;
  padding: $spacing-base * 4 0;
  
  &__container {
    max-width: 64rem;
    margin: 0 auto;
    padding: 0 $spacing-base;
    text-align: center;
  }

  &__title {
    font-size: 2.5rem;
    font-weight: 700;
    margin-bottom: $spacing-base;
  }

  &__subtitle {
    font-size: 1.25rem;
    opacity: 0.9;
  }
}

// Content Container
.beliefs-content {
  max-width: 64rem;
  margin: 0 auto;
  padding: $spacing-base * 3 $spacing-base;
}

// Introduction
.beliefs-intro {
  @include section-spacing;
  text-align: center;
  max-width: 48rem;
  margin: 0 auto;

  &__text {
    font-size: 1.125rem;
    color: $text-light;
    line-height: 1.7;
  }
}

// Section Styles
.beliefs-section {
  @include section-spacing;

  &__title {
    font-size: 1.875rem;
    font-weight: 700;
    color: $text-dark;
    text-align: center;
    margin-bottom: $spacing-base * 2;
  }

  &__intro {
    text-align: center;
    color: $text-light;
    margin-bottom: $spacing-base * 2;
    font-size: 1.125rem;
  }
}

// Core Beliefs Grid
.beliefs-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: $spacing-base * 1.5;

  @media (min-width: 640px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (min-width: 1024px) {
    grid-template-columns: repeat(3, 1fr);
  }
}

// Belief Cards
.belief-card {
  background: $white;
  padding: $spacing-base * 1.5;
  border-radius: $border-radius;
  @include card-shadow;

  &__title {
    font-size: 1.25rem;
    font-weight: 600;
    color: $primary-color;
    margin-bottom: $spacing-base;
    display: flex;
    align-items: center;
    gap: $spacing-base * 0.5;

    i {
      color: $secondary-color;
    }
  }

  &__text {
    color: $text-light;
    line-height: 1.6;
  }
}

// Baptist Distinctives
.distinctives-list {
  display: flex;
  flex-direction: column;
  gap: $spacing-base * 1.5;
}

.distinctive-item {
  background: $white;
  padding: $spacing-base * 1.5;
  border-radius: $border-radius;
  @include card-shadow;

  &__title {
    font-size: 1.25rem;
    font-weight: 600;
    color: $primary-color;
    margin-bottom: $spacing-base * 0.5;
  }

  &__text {
    color: $text-light;
    line-height: 1.6;
  }
}

// Scripture Section
.scripture-section {
  text-align: center;
}

.scripture-card {
  background: $white;
  padding: $spacing-base * 2;
  border-radius: $border-radius;
  @include card-shadow;
  max-width: 48rem;
  margin: 0 auto;

  .scripture-text {
    font-size: 1.25rem;
    color: $text-dark;
    font-style: italic;
    margin-bottom: $spacing-base;
    line-height: 1.6;
  }

  .scripture-reference {
    color: $secondary-color;
    font-weight: 600;
  }
}
</style> 